import React, { useEffect, useMemo, useRef, useState } from "react";
import { customFetch } from "../../utils/Auth";
import { trackExternalEvent } from "../../utils/logging";
import { environment } from "../../config/environment";

export default function VAEvalCaseQuery({
  reviewedCaseMap = {},
  onToggleReviewedCase,
  onCaseLoaded,
  onNoCases,
  onErrorMessageChange,
  view = "",
}) {
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [evalCases, setEvalCases] = useState([]);
  const [activeCasePatientId, setActiveCasePatientId] = useState("");
  const caseDetailsCacheRef = useRef({});
  const abortControllerRef = useRef(null);
  const referenceDate = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const filteredCases = useMemo(() => {
    const query = String(searchValue || "").trim().toLowerCase();
    if (!query) {
      return evalCases;
    }

    return evalCases.filter((evalCase) => {
      const appointmentId = String(evalCase.appointment_id || "").toLowerCase();
      const patientId = String(evalCase.patient_id || "").toLowerCase();
      return appointmentId.includes(query) || patientId.includes(query);
    });
  }, [evalCases, searchValue]);

  const publishError = (message) => {
    if (typeof onErrorMessageChange === "function") {
      onErrorMessageChange(String(message || ""));
    }
  };

  const fetchEvalCases = async (searchTerm = "", signal) => {
    const normalizedSearchTerm = String(searchTerm || "").trim();
    const searchParams = new URLSearchParams({
      reference_date: referenceDate,
    });
    if (normalizedSearchTerm) {
      searchParams.set("search_term", normalizedSearchTerm);
    }
    if (view) {
      searchParams.set("view", view);
    }

    const response = await customFetch(
      `${environment.apiUrl}/patients/va-eval/cases?${searchParams.toString()}`,
      { method: "GET", signal },
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Failed to load eval cases");
    }

    return Array.isArray(data?.cases) ? data.cases : [];
  };

  const fetchEvalCaseDetail = async (patientId, signal) => {
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId) {
      throw new Error("Invalid patient id");
    }

    if (caseDetailsCacheRef.current[normalizedPatientId]) {
      return caseDetailsCacheRef.current[normalizedPatientId];
    }

    const searchParams = new URLSearchParams({ reference_date: referenceDate });
    if (view) {
      searchParams.set("view", view);
    }
    const response = await customFetch(
      `${environment.apiUrl}/patients/va-eval/cases/${encodeURIComponent(normalizedPatientId)}?${searchParams.toString()}`,
      { method: "GET", signal },
    );
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Failed to load case detail");
    }

    caseDetailsCacheRef.current[normalizedPatientId] = data;
    return data;
  };

  const loadCaseByPatientId = async (
    patientId,
    caseListOverride = evalCases,
    options = {},
  ) => {
    const { manageLoading = true, signal = abortControllerRef.current?.signal } =
      options;
    const normalizedPatientId = String(patientId || "").trim();
    if (!normalizedPatientId || !Array.isArray(caseListOverride)) {
      return;
    }

    const evalCase = caseListOverride.find(
      (candidate) =>
        String(candidate?.patient_id || "").trim() === normalizedPatientId,
    );
    if (!evalCase) {
      return;
    }

    if (manageLoading) {
      setIsLoading(true);
    }
    publishError("");

    try {
      const caseDetail = await fetchEvalCaseDetail(normalizedPatientId, signal);
      if (signal?.aborted) {
        return;
      }
      setActiveCasePatientId(normalizedPatientId);
      if (typeof onCaseLoaded === "function") {
        onCaseLoaded({
          patientId: normalizedPatientId,
          appointmentId: String(evalCase.appointment_id || "").trim(),
          recordingDateTimeLabel:
            String(caseDetail?.recording_datetime_label || "").trim() ||
            String(evalCase?.recording_datetime_label || "").trim(),
          caseDetail,
        });
      }
      void trackExternalEvent("va_eval:case_selected");
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      console.error("Failed to load eval case detail:", error);
      publishError("Unable to load selected case.");
    } finally {
      if (manageLoading && !signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  const loadFirstFilteredCase = async () => {
    const normalizedQuery = String(searchValue || "").trim();
    if (normalizedQuery) {
      void trackExternalEvent("va_eval:appointment_search_submitted");
    }
    if (!normalizedQuery) {
      if (filteredCases.length === 0) {
        publishError("No matching appointment or patient found.");
        return;
      }

      await loadCaseByPatientId(filteredCases[0].patient_id, evalCases);
      return;
    }

    const signal = abortControllerRef.current?.signal;
    setIsLoading(true);
    publishError("");
    try {
      const loadedCases = await fetchEvalCases(normalizedQuery, signal);
      if (signal?.aborted) {
        return;
      }
      setEvalCases(loadedCases);
      caseDetailsCacheRef.current = {};

      if (loadedCases.length === 0) {
        setActiveCasePatientId("");
        if (typeof onNoCases === "function") {
          onNoCases();
        }
        publishError("No matching appointment or patient found.");
        void trackExternalEvent("va_eval:appointment_search_no_results");
        return;
      }

      await loadCaseByPatientId(loadedCases[0].patient_id, loadedCases, {
        manageLoading: false,
        signal,
      });
    } catch (error) {
      if (signal?.aborted) {
        return;
      }
      console.error("Failed to query eval cases by appointment:", error);
      publishError("Unable to load eval cases right now.");
      void trackExternalEvent("va_eval:appointment_search_failed");
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const { signal } = controller;

    const fetchInitialCases = async () => {
      setIsLoading(true);
      publishError("");
      setSearchValue("");

      try {
        const loadedCases = await fetchEvalCases("", signal);
        if (signal.aborted) {
          return;
        }
        setEvalCases(loadedCases);
        caseDetailsCacheRef.current = {};

        if (loadedCases.length === 0) {
          setActiveCasePatientId("");
          if (typeof onNoCases === "function") {
            onNoCases();
          }
          publishError("No eval-ready cases found in the last 30 days.");
          void trackExternalEvent("va_eval:list_loaded_empty");
          return;
        }

        void trackExternalEvent("va_eval:list_loaded");

        await loadCaseByPatientId(loadedCases[0].patient_id, loadedCases, {
          manageLoading: false,
          signal,
        });
      } catch (error) {
        if (signal.aborted) {
          return;
        }
        console.error("Failed to fetch eval cases:", error);
        setEvalCases([]);
        setActiveCasePatientId("");
        if (typeof onNoCases === "function") {
          onNoCases();
        }
        publishError("Unable to load eval cases right now.");
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
          setIsInitialLoading(false);
        }
      }
    };

    void fetchInitialCases();
    return () => controller.abort();
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isInitialLoading) {
    return (
      <div className="bg-white shadow-knowtex-shadow rounded-xl p-4 min-h-[200px] h-[34vh] max-h-[320px] xl:h-[82vh] xl:max-h-none flex flex-col">
        <h2 className="text-[20px] font-semibold mb-3">Recordings</h2>
        <div className="flex-1 border border-gray-100 rounded-xl bg-gray-50/60 flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 rounded-full border-4 border-ktx-purple/20 border-t-ktx-purple animate-spin" />
          <p className="text-sm font-medium text-ktx-navy/80">
            Loading recordings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-knowtex-shadow rounded-xl p-4 min-h-[200px] h-[34vh] max-h-[320px] xl:h-[82vh] xl:max-h-none flex flex-col">
      <h2 className="text-[20px] font-semibold mb-3">Recordings</h2>
      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Appointment ID | Patient ID"
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              void loadFirstFilteredCase();
            }
          }}
          className="h-10 flex-1 rounded-lg border-none bg-gray-100 px-4 text-ktx-navy placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
        />
        <button
          type="button"
          onClick={() => {
            void loadFirstFilteredCase();
          }}
          disabled={isLoading || filteredCases.length === 0}
          className="h-10 bg-ktx-purple hover:bg-ktx-navy disabled:bg-gray-300 text-white rounded-lg px-4 font-semibold"
        >
          Go
        </button>
      </div>

      <div className="flex-1 overflow-auto border border-gray-100 rounded-xl">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-gray-200 text-left text-gray-600">
              <th className="px-4 py-3 font-semibold">Appointment</th>
              <th className="px-4 py-3 font-semibold text-center">Reviewed</th>
            </tr>
          </thead>
          <tbody>
            {filteredCases.map((evalCase) => {
              const patientId = String(evalCase.patient_id || "");
              const isActive = patientId === String(activeCasePatientId || "");
              const isReviewed = Boolean(reviewedCaseMap[patientId]);

              return (
                <tr
                  key={patientId}
                  className={`border-b border-gray-100 cursor-pointer ${
                    isActive ? "bg-barely-purple" : "hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    void loadCaseByPatientId(patientId);
                  }}
                >
                  <td className="px-4 py-3 text-ktx-navy font-medium">
                    <p>{evalCase.appointment_id || "No appointment ID"}</p>
                    <p className="mt-1 text-xs font-normal text-gray-500">
                      Patient {String(evalCase.patient_id || "-")}
                    </p>
                    {Number(evalCase.job_count || 0) > 1 && (
                      <p className="mt-1 text-xs font-normal text-gray-500">
                        {Number(evalCase.job_count)} recordings
                      </p>
                    )}
                    {view === "all" && Array.isArray(evalCase.role) && (
                      <p className="mt-1 text-xs font-normal text-gray-500">
                        {evalCase.role.includes("documentation") &&
                        evalCase.role.includes("coding")
                          ? "Documentation + Coding"
                          : evalCase.role.includes("coding")
                            ? "Coding"
                            : "Documentation"}
                      </p>
                    )}
                    {String(evalCase.recording_datetime_label || "").trim() && (
                      <p className="mt-1 text-xs font-normal text-gray-500">
                        Recorded {String(evalCase.recording_datetime_label || "")}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (typeof onToggleReviewedCase === "function") {
                          onToggleReviewedCase(patientId);
                        }
                      }}
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${
                        isReviewed
                          ? "bg-ktx-purple border-ktx-purple text-white"
                          : "bg-white border-gray-300 text-gray-300"
                      }`}
                      aria-label={
                        isReviewed ? "Mark as not reviewed" : "Mark as reviewed"
                      }
                    >
                      <svg
                        className="w-4 h-4"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="m5 13 4 4L19 7"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredCases.length === 0 && (
              <tr>
                <td
                  colSpan={2}
                  className="px-4 py-6 text-center text-gray-500 font-medium"
                >
                  No matching recordings.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
